import { useMemo } from 'react';
import { Tooltip } from 'react-tooltip';
import { Badge } from 'common/components/badge/Badge';
import { KWELLO_ICON, KwelloSvgIcon } from 'common/components/icon/KwelloSvgIcon';
import ClickableDiv from 'common/components/clickableDiv/ClickableDiv';

import { FilterType, CommonFilterList } from 'common/models/filter.model';
import useFilterClick from 'common/hooks/useFilterCLick';
import { GridCellProps } from '@progress/kendo-react-grid';

export enum SentimentLabel {
  PENDING = 'Pending',
  POSITIVE = 'Positive',
  CAUTIOUS = 'Cautious',
  NEUTRAL = 'Neutral',
  NEGATIVE = 'Negative',
  NOT_AVAILABLE = 'Not Available',
}

export const NotAvailableReason =
  'The response lacks sufficient detail or context to determine sentiment effectively.';

export const PendingReason =
  'Sentiment analysis is in progress. The result will be available soon when processing is finished.';

function SentimentCell({ dataItem }: GridCellProps) {
  const responseId = dataItem?.responseId;
  const sentimentLabel = dataItem?.sentimentLabel ?? 'Pending';
  const onFilterClick = useFilterClick();

  const sentimentBadgeIcon = useMemo(() => {
    switch (sentimentLabel) {
      case SentimentLabel.PENDING:
        return KWELLO_ICON.HOURGLASS_TIMER;
      case SentimentLabel.POSITIVE:
        return KWELLO_ICON.POSITIVE_ICON;
      case SentimentLabel.CAUTIOUS:
        return KWELLO_ICON.WARNING_ICON;
      case SentimentLabel.NEUTRAL:
        return KWELLO_ICON.NEUTRAL_ICON;
      case SentimentLabel.NEGATIVE:
        return KWELLO_ICON.ALERT_CIRCLE;
      default:
        return KWELLO_ICON.HOURGLASS_TIMER;
    }
  }, [sentimentLabel]);

  const sentimentReason = useMemo(() => {
    if (sentimentLabel === SentimentLabel.NOT_AVAILABLE) {
      return NotAvailableReason;
    }
    if (sentimentLabel === SentimentLabel.PENDING) {
      return `${PendingReason} Pending badges are not clickable or filterable.`;
    }
    return dataItem?.sentimentReason as string;
  }, [sentimentLabel, dataItem?.sentimentReason]);

  const onClickBadge = () => {
    if (onFilterClick && sentimentLabel !== SentimentLabel.PENDING) {
      onFilterClick({
        filterType: FilterType.AUTOCOMPLETE,
        filterKey: CommonFilterList.Sentiment,
        filterLabel: 'Sentiment',
        value: [{ name: sentimentLabel, value: dataItem?.sentimentType ?? 0 }],
      });
    }
  };

  if (!responseId) {
    return null;
  }

  return (
    <td className="k-table-td">
      <Tooltip id={`sentiment-${responseId}`} place="top" positionStrategy={'fixed'}>
        <div>{sentimentReason}</div>
      </Tooltip>
      <div className="flex-center" data-tooltip-id={`sentiment-${responseId}`}>
        <ClickableDiv
          onClick={onClickBadge}
          className={
            sentimentLabel === SentimentLabel.PENDING
              ? 'sentiment-pending'
              : 'non-pending-sentiment'
          }
        >
          <Badge className={sentimentLabel.replace(' ', '-')}>
            {sentimentLabel !== SentimentLabel.NOT_AVAILABLE ? (
              <div className="badge-label-with-icon">
                {KwelloSvgIcon({
                  icon: sentimentBadgeIcon,
                })}
                {sentimentLabel}
              </div>
            ) : (
              <div className="badge-label-with-icon">
                <span className="badge-label">{sentimentLabel}</span>
              </div>
            )}
          </Badge>
        </ClickableDiv>
      </div>
    </td>
  );
}

export default SentimentCell;
